import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Session } from '@models/Sessions/Session';
import { Team } from '@models/Sessions/Team';
import { ConfigService } from '@services/config.service';
import { EChartsOption, EChartsType } from 'echarts';
import _ from 'lodash';

@Component({
  selector: 'app-ideas-matrix',
  templateUrl: './ideas-matrix.component.html',
  styleUrls: ['./ideas-matrix.component.scss'],
})
export class SessionModulesIdeasMatrixComponent implements OnInit {
  @Input() session: Session;
  @Output() setNodesData = new EventEmitter<any>();
  @Output() ideaSelected = new EventEmitter<any>();

  allFilter = { by: 'all' };
  filter = [this.allFilter] as any[];
  selectedGroup = 'everyone';

  tooManyIdeas = false;
  nodesData = [];

  myTeam: Team;

  echart: EChartsType;
  chartOptions: EChartsOption = {
    grid: {
      top: 10,
      right: 150,
      bottom: 60,
      left: 60,
    },
    xAxis: {
      max: 10,
      name: 'Attractiveness',
      nameLocation: 'middle' as const,
      nameGap: 20,
      nameTextStyle: {
        fontSize: 18,
        fontWeight: 'bold' as const,
      },
    },
    yAxis: {
      max: 10,
      name: 'Feasibility',
      nameLocation: 'middle' as const,
      nameGap: 20,
      nameTextStyle: {
        fontSize: 18,
        fontWeight: 'bold' as const,
      },
    },
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: [0],
      },
      {
        type: 'inside',
        yAxisIndex: [0],
      },
    ],
  };
  chartMerge: Pick<EChartsOption, 'series'>;

  private series = {
    type: 'scatter' as const,
    symbolSize: 15,
    emphasis: {
      focus: 'self' as const,
    },
    labelLayout: () => ({
      x: this.echart.getWidth() - 130,
      hideOverlap: true,
      moveOverlap: 'shiftY' as const,
    }),
    labelLine: {
      show: true,
      length2: 30,
      lineStyle: {
        color: '#bbb',
      },
    },
    label: {
      show: true,
      width: 120,
      overflow: 'break' as const,
      formatter: ({ data }) => data[2],
      minMargin: 10,
    },
  };

  constructor(public appConfig: ConfigService) {}

  ngOnInit() {
    this.myTeam = this.session.config.teams.find(
      t => t._id === this.session.me.team,
    );
    this.redraw();
  }

  selectFilter(filterObject) {
    // if other than all selected, deselect all
    if (filterObject.by !== 'all') {
      const index = this.getObjectIndex(this.allFilter);
      if (index >= 0) {
        this.filter.splice(index, 1);
      }
    }

    if (this.getObjectIndex(filterObject) < 0) {
      this.filter.push(filterObject);
    } else {
      const index = this.getObjectIndex(filterObject);
      this.filter.splice(index, 1);
    }

    // if all selected, unselect all others
    if (filterObject.by === 'all') {
      this.filter = [];
      this.filter.push(this.allFilter);
    }

    // if mine selected, unselect my team
    if (
      filterObject.by === 'participant_id' &&
      filterObject.value === this.session.me._id
    ) {
      const index = this.getObjectIndex({
        by: 'team',
        value: this.session.me.team,
      });
      if (index >= 0) {
        this.filter.splice(index, 1);
      }
    }

    // if my team selected, unselect mine
    if (
      filterObject.by === 'team' &&
      filterObject.value === this.session.me.team
    ) {
      const index = this.getObjectIndex({
        by: 'participant_id',
        value: this.session.me._id,
      });
      if (index >= 0) {
        this.filter.splice(index, 1);
      }
    }

    this.redraw();
  }

  isFilterSelected(filterObject) {
    const index = this.getObjectIndex(filterObject);
    if (index < 0) {
      return false;
    }
    return true;
  }

  getObjectIndex(o) {
    for (let i = 0; i < this.filter.length; i++) {
      if (
        this.filter[i].by === o.by &&
        (this.filter[i] as any).value === o.value
      ) {
        return i;
      }
    }
    return -1;
  }

  // Todo: Change detection

  getVoting(idea) {
    if (this.selectedGroup === 'moderators') {
      return { x: idea.voting.moderators.x, y: idea.voting.moderators.y };
    } else if (this.selectedGroup === 'participants') {
      return { x: idea.voting.participants.x, y: idea.voting.participants.y };
    } else if (this.selectedGroup === 'external evaluators') {
      return { x: idea.voting.externals.x, y: idea.voting.externals.y };
    } else {
      return { x: idea.voting.x, y: idea.voting.y };
    }
  }

  redraw() {
    // Create a look up object from the participants
    const participants = _.keyBy(this.session.config.participants, '_id');

    // Extract the ideas we want
    const ideas = _.filter(this.session.data.ideas, idea => {
      const voting = this.getVoting(idea);
      return (voting.x || voting.y) && idea.published;
    });

    // array of filtered ideas
    let filteredIdeas = [];

    _.each(this.filter, filter => {
      if (filter.by === 'all') {
        filteredIdeas = ideas;
      } else if (filter.by === 'participant_id') {
        const fi = _.filter(ideas, { created_by: filter.value });
        _.each(fi, idea => {
          filteredIdeas.push(idea);
        });
      } else if (filter.by === 'team') {
        const fi = _.filter(ideas, idea => {
          return (
            participants[idea.created_by] &&
            participants[idea.created_by].team === filter.value
          );
        });
        _.each(fi, idea => {
          filteredIdeas.push(idea);
        });
      }
    });

    // TODO this is not cool, use proper archiving
    filteredIdeas = _.sortBy(filteredIdeas, idea => {
      return new Date(idea.last_changed);
    });
    this.tooManyIdeas = filteredIdeas.length >= this.appConfig.MAX_IDEAS;
    filteredIdeas = _.takeRight(filteredIdeas, this.appConfig.MAX_IDEAS);

    // Prepare Node data
    const nodes_data = [];
    _.forEach(filteredIdeas, idea => {
      const voting = this.getVoting(idea);
      const node = {} as any;
      node.x = voting.x;
      node.y = voting.y;
      node.text = idea.title;
      node.r = 8;
      node.idea_id = idea._id;
      node.forked_from = idea.forked_from;
      node.created_by = idea.created_by;
      if (participants[idea.created_by]) {
        if ((this.filter as any).by === 'team') {
          node.creating_entity =
            this.session.config.teams[
              participants[idea.created_by].team as string
            ].name;
        } else {
          // If we filter by a participant or show all ideas...
          node.creating_entity = participants[idea.created_by].user.name;
        }
      } else {
        // If we can't find the participant the idea is probalby imported
        node.creating_entity = 'Imported';
      }
      node.color = node.creating_entity;
      nodes_data.push(node);
    });

    this.setNodesData.emit(nodes_data);
    this.nodesData = nodes_data;

    this.chartMerge = {
      series: [
        {
          ...this.series,
          data: this.nodesData.map(n => [n.x, n.y, n.text, n.idea_id]),
        },
      ],
    };
  }

  onChartClick(event: any) {
    this.ideaSelected.emit(
      _.find(this.session.data.ideas, { _id: event.data[3] }),
    );
  }
}
